import { useState } from 'react';
import { Button, Flex, Loader, Modal, ScrollArea, Stack, Text } from '@mantine/core';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { isValidEmail } from '@dealstack/shared/lib/validation';
import {
	BulkInviteAuthorisedSignatoriesPreviewRes,
	BulkInviteAuthorisedSignatoryRes,
} from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { BulkInviteAuthorisedSignatoryRoleSection } from './BulkInviteAuthorisedSignatoryRoleSection';
import {
	buildBulkInviteConfirmPayload,
	countDuplicateSignatorySkips,
} from './BulkInviteAuthorisedSignatoryModal.utils';
import type {
	BulkInviteAuthorisedSignatoryConfirmPayload,
	RoleCancellations,
	RoleInput,
} from './BulkInviteAuthorisedSignatoryModal.types';

const emptyRoleInput: RoleInput = { email: '', firstName: '', lastName: '' };

interface Props {
	preview: BulkInviteAuthorisedSignatoriesPreviewRes | null;
	isLoadingPreview: boolean;
	isSubmitting: boolean;
	result: BulkInviteAuthorisedSignatoryRes | null;
	onClose: () => void;
	onConfirm: (payload: BulkInviteAuthorisedSignatoryConfirmPayload) => void;
}

export const BulkInviteAuthorisedSignatoryModal = ({
	preview,
	isLoadingPreview,
	isSubmitting,
	result,
	onClose,
	onConfirm,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const [inputs, setInputs] = useState<Record<string, RoleInput>>({});
	const [cancelledEmailsByRole, setCancelledEmailsByRole] = useState<RoleCancellations>({});

	const patch = (roleName: string, p: Partial<RoleInput>) =>
		setInputs((prev) => ({
			...prev,
			[roleName]: { ...emptyRoleInput, ...prev[roleName], ...p },
		}));

	const toggleCancel = (roleName: string, email: string) => {
		setCancelledEmailsByRole((prev) => {
			const current = prev[roleName] ?? [];
			const isMarked = current.includes(email);
			return {
				...prev,
				[roleName]: isMarked
					? current.filter((value) => value !== email)
					: [...current, email],
			};
		});
	};

	const confirmPayload = preview
		? buildBulkInviteConfirmPayload(preview, inputs, cancelledEmailsByRole)
		: { changes: [], cancellations: [] };
	const hasSubmittable =
		confirmPayload.changes.length > 0 || confirmPayload.cancellations.length > 0;

	const handleConfirm = () => {
		if (!hasSubmittable) {
			return;
		}
		onConfirm(confirmPayload);
	};

	const renderResult = (r: BulkInviteAuthorisedSignatoryRes) => {
		const duplicateSignatorySkips = countDuplicateSignatorySkips(r.results);

		return (
			<Flex direction="column" gap="md" p="md">
				<Text
					size="sm"
					data-testid={TEST_ELEMENT_IDS.BULK_INVITE_AUTHORISED_SIGNATORY_RESULT_SUMMARY}
				>
					{tMipManage(
						'documents_page.bulk_invite_authorised_signatory_modal.result_summary',
						{
							invited: r.invitedSignerCount,
							emails: r.emailsSent,
							skipped: r.skipped,
						}
					)}
				</Text>
				{duplicateSignatorySkips > 0 && (
					<Text
						size="sm"
						data-testid={
							TEST_ELEMENT_IDS.BULK_INVITE_AUTHORISED_SIGNATORY_RESULT_SKIPPED_DUPLICATE
						}
					>
						{tMipManage(
							'documents_page.bulk_invite_authorised_signatory_modal.result_skipped_duplicate_signatory',
							{ count: duplicateSignatorySkips }
						)}
					</Text>
				)}
				<Flex justify="flex-end">
					<Button
						data-testid={
							TEST_ELEMENT_IDS.BULK_INVITE_AUTHORISED_SIGNATORY_RESULT_CLOSE_BUTTON
						}
						onClick={onClose}
					>
						{tMipManage(
							'documents_page.bulk_invite_authorised_signatory_modal.close_button'
						)}
					</Button>
				</Flex>
			</Flex>
		);
	};

	const renderForm = () => {
		if (isLoadingPreview || !preview) {
			return (
				<Flex justify="center" p="xl">
					<Loader />
				</Flex>
			);
		}
		return (
			<Flex direction="column" gap="md" p="md">
				<Text size="sm">
					{tMipManage(
						'documents_page.bulk_invite_authorised_signatory_modal.description',
						{
							count: preview.totalDocuments,
						}
					)}
				</Text>
				<ScrollArea.Autosize mah={400}>
					<Stack gap="lg">
						{preview.roles.map((role) => (
							<BulkInviteAuthorisedSignatoryRoleSection
								key={role.roleName}
								role={role}
								totalDocuments={preview.totalDocuments}
								value={inputs[role.roleName] ?? emptyRoleInput}
								emailValid={isValidEmail(
									(inputs[role.roleName]?.email ?? '').trim()
								)}
								onChange={(p) => patch(role.roleName, p)}
								cancelledEmails={cancelledEmailsByRole[role.roleName] ?? []}
								onToggleCancel={(email) => toggleCancel(role.roleName, email)}
							/>
						))}
					</Stack>
				</ScrollArea.Autosize>
				<Flex gap="sm" justify="flex-end">
					<Button variant="subtle" onClick={onClose} disabled={isSubmitting}>
						{tMipManage(
							'documents_page.bulk_invite_authorised_signatory_modal.cancel_button'
						)}
					</Button>
					<Button
						data-testid={
							TEST_ELEMENT_IDS.BULK_INVITE_AUTHORISED_SIGNATORY_CONFIRM_BUTTON
						}
						variant="filled"
						disabled={!hasSubmittable || isSubmitting}
						loading={isSubmitting}
						onClick={handleConfirm}
					>
						{tMipManage(
							'documents_page.bulk_invite_authorised_signatory_modal.confirm_button'
						)}
					</Button>
				</Flex>
			</Flex>
		);
	};

	return (
		<Modal.Root opened onClose={onClose} centered size="lg">
			<Modal.Overlay />
			{/* data-testid goes on Content, not Root: Root has no box of its own, so a Playwright
			    visibility check against it never resolves even while the modal is genuinely open. */}
			<Modal.Content data-testid={TEST_ELEMENT_IDS.BULK_INVITE_AUTHORISED_SIGNATORY_MODAL}>
				<Modal.Header>
					<Modal.Title>
						{tMipManage(
							'documents_page.bulk_invite_authorised_signatory_modal.assign_title'
						)}
					</Modal.Title>
					<Modal.CloseButton />
				</Modal.Header>
				<Modal.Body>{result ? renderResult(result) : renderForm()}</Modal.Body>
			</Modal.Content>
		</Modal.Root>
	);
};
